/* De site direct verversen na het opslaan (ADR 0031).
 *
 * De site bouwt haar pagina's elke tien minuten opnieuw op. Na een geslaagde opslag vraagt de CRM de
 * edge function `website-ververs` om de site meteen te verversen. Die function houdt het geheim; de
 * browser ziet het nooit.
 *
 * Het opslaan is leidend. De verversing start pas als de opslag gelukt is, het formulier wacht er
 * niet op, en een mislukte of trage verversing maakt van een geslaagde opslag nooit een fout: de
 * wijziging staat er dan na hooguit tien minuten alsnog.
 *
 * De client komt als argument binnen, zoals in data/website.ts, zodat de toetsen hem kunnen nabootsen. */

import {
  maakDoorverwijzing, verwijderDoorverwijzing, wijzigDoorverwijzing, type Concept as DoorverwijzingConcept, type Doorverwijzing, type Gewijzigd, type Wijziging,
} from './data/doorverwijzingen';
import { slaInstellingenOp, type Concept, type InstellingenOpgeslagen, type Stand } from './data/instellingen';
import { wijzigMedia, type Gewijzigd as MediaGewijzigd, type Invoer as MediaInvoer, type MediaBestand } from './data/media';
import { slaPaginaOp, type PaginaOpgeslagen } from './data/paginas';
import { kentPublicatie, maakNieuw, slaOp, type ContentSoort, type Opgeslagen, type Rij, type WebsiteClient } from './data/website';

export const FUNCTIE = 'website-ververs';
/** Zo lang wacht de CRM op de function. Die wacht zelf hooguit vijf seconden op de site. */
export const WACHT_MS = 10_000;

export interface VerversClient {
  functions: { invoke(naam: string, opties?: { method?: 'POST' }): Promise<{ data: unknown; error: unknown }> };
}

/** Wat er met de site gebeurd is. `mislukt` en `niet_ingesteld` zijn geen fout voor de gebruiker. */
export type Verversing =
  | { uitkomst: 'gelukt' }
  | { uitkomst: 'niet_ingesteld' }
  | { uitkomst: 'mislukt'; reden: string };

export interface MetVerversing {
  /** De uitkomst van het verversen, of null als de site niet hoefde te verversen (een concept).
   *  Deze belofte wordt altijd vervuld en gooit nooit. */
  verversing: Promise<Verversing> | null;
}

/** De site toont alleen wat gepubliceerd is. Was het een concept en blijft het een concept, dan
 *  verandert er op de site niets. Van publiceren en van offline halen merkt de site wel iets. Een
 *  soort zonder publicatie (veelgestelde vragen) staat altijd op de site, en een rij die de kolom nog
 *  niet heeft (een teamlid van vóór het script) ook. */
export const moetVerversen = (soort: ContentSoort, vorige: Rij | null, rij: Rij): boolean =>
  !kentPublicatie(soort, rij) || vorige?.published === true || rij.published === true;

const mislukt = (reden: string): Verversing => ({ uitkomst: 'mislukt', reden });

/** Een antwoord van de function dat geen 2xx is (bv. 401, 403, of 404 als hij nog niet is uitgerold)
 *  draagt het antwoord in `context`; een netwerkfout niet. */
function redenVan(fout: unknown): string {
  const status = (fout as { context?: { status?: unknown } } | null)?.context?.status;
  return typeof status === 'number' ? `status ${status}` : 'geen verbinding';
}

function lees(data: unknown): Verversing {
  const d = data as { uitkomst?: unknown; reden?: unknown } | null;
  if (d?.uitkomst === 'gelukt' || d?.uitkomst === 'niet_ingesteld') return { uitkomst: d.uitkomst };
  if (d?.uitkomst === 'mislukt') return mislukt(typeof d.reden === 'string' ? d.reden.slice(0, 80) : 'onbekend');
  return mislukt('onverwacht antwoord');
}

/** Vraagt de function om de site te verversen. Gooit nooit en wacht hooguit `wachtMs`. */
export async function ververs(client: VerversClient, wachtMs = WACHT_MS): Promise<Verversing> {
  let klok: ReturnType<typeof setTimeout> | undefined;
  const teLaat = new Promise<Verversing>((klaar) => {
    klok = setTimeout(() => klaar(mislukt('geen antwoord op tijd')), wachtMs);
  });
  const aanroep = (async (): Promise<Verversing> => {
    try {
      // Geen body: de function ververst alles en leest niets uit het verzoek.
      const { data, error } = await client.functions.invoke(FUNCTIE, { method: 'POST' });
      return error ? mislukt(redenVan(error)) : lees(data);
    } catch {
      return mislukt('geen verbinding');
    }
  })();
  try {
    return await Promise.race([aanroep, teLaat]);
  } finally {
    clearTimeout(klok);
  }
}

const naOpslaan = (client: VerversClient, soort: ContentSoort, vorige: Rij | null, rij: Rij): Promise<Verversing> | null =>
  moetVerversen(soort, vorige, rij) ? ververs(client) : null;

/** Slaat op (data/website.ts) en start daarna de verversing, zonder erop te wachten. Mislukt het
 *  opslaan, dan gooit dit en gaat er geen aanroep naar de function. */
export async function slaOpEnVervers(client: WebsiteClient & VerversClient, soort: ContentSoort, vorige: Rij, concept: Rij): Promise<Opgeslagen & MetVerversing> {
  const opgeslagen = await slaOp(client, soort, vorige, concept);
  return { ...opgeslagen, verversing: naOpslaan(client, soort, vorige, opgeslagen.rij) };
}

/** Maakt iets nieuws aan. Met een publicatievlag is dat altijd een concept en hoeft de site niets te
 *  doen (ook bij een nieuw teamlid); een nieuwe veelgestelde vraag staat meteen op de site. */
export async function maakNieuwEnVervers(client: WebsiteClient & VerversClient, soort: ContentSoort, concept: Rij): Promise<{ rij: Rij } & MetVerversing> {
  const rij = await maakNieuw(client, soort, concept);
  return { rij, verversing: naOpslaan(client, soort, null, rij) };
}

/** Slaat de instellingen van de site op (data/instellingen.ts) en start daarna de verversing. Een
 *  instelling kent geen concept: elke wijziging raakt de site. Is er niets gewijzigd, dan is er niets
 *  geschreven en gaat er ook geen aanroep naar de function. */
export async function slaInstellingenOpEnVervers(client: WebsiteClient & VerversClient, stand: Stand, concept: Concept): Promise<InstellingenOpgeslagen & MetVerversing> {
  const opgeslagen = await slaInstellingenOp(client, stand, concept);
  return { ...opgeslagen, verversing: opgeslagen.geschreven.length > 0 ? ververs(client) : null };
}

/** Slaat een vaste pagina op (data/paginas.ts) en start daarna de verversing. Een pagina kent geen
 *  concept: elke wijziging raakt de site. Is er niets gewijzigd, dan is er niets geschreven en gaat er
 *  ook geen aanroep naar de function. */
export async function slaPaginaOpEnVervers(client: WebsiteClient & VerversClient, vorige: Rij, concept: Rij): Promise<PaginaOpgeslagen & MetVerversing> {
  const opgeslagen = await slaPaginaOp(client, vorige, concept);
  return { ...opgeslagen, verversing: opgeslagen.geschreven.length > 0 ? ververs(client) : null };
}

/** Maakt een doorverwijzing aan (data/doorverwijzingen.ts) en start daarna de verversing. Een
 *  doorverwijzing kent geen concept: de site leest de hele tabel. */
export async function maakDoorverwijzingEnVervers(client: WebsiteClient & VerversClient, concept: DoorverwijzingConcept): Promise<{ rij: Doorverwijzing } & MetVerversing> {
  const rij = await maakDoorverwijzing(client, concept);
  return { rij, verversing: ververs(client) };
}

/** Wijzigt een doorverwijzing en start daarna de verversing. De site leest het doel en de status,
 *  niet de notitie: is alleen de notitie gewijzigd, of niets, dan gaat er geen aanroep naar de function. */
export async function wijzigDoorverwijzingEnVervers(client: WebsiteClient & VerversClient, vorige: Doorverwijzing, concept: Wijziging): Promise<Gewijzigd & MetVerversing> {
  const gewijzigd = await wijzigDoorverwijzing(client, vorige, concept);
  return { ...gewijzigd, verversing: gewijzigd.geschreven.some((kolom) => kolom !== 'note') ? ververs(client) : null };
}

/** Verwijdert een doorverwijzing (data/doorverwijzingen.ts) en start daarna de verversing: de site
 *  bewaart de tabel zelf ook tien minuten. Is er niets verwijderd, dan gooit dit en gaat er geen
 *  aanroep naar de function. */
export async function verwijderDoorverwijzingEnVervers(client: WebsiteClient & VerversClient, van: string): Promise<{ rij: Doorverwijzing } & MetVerversing> {
  const rij = await verwijderDoorverwijzing(client, van);
  return { rij, verversing: ververs(client) };
}

/** Wijzigt de naam of de beschrijving van een bestand uit de media (data/media.ts) en start daarna de
 *  verversing als de beschrijving is gewijzigd: die gebruikt de site bij het beeld. De naam gebruikt de
 *  site niet: is alleen die gewijzigd, of niets, dan gaat er geen aanroep naar de function. Uploaden
 *  ververst nooit: een nieuw bestand staat nog nergens op de site. De site leest de beschrijvingen bij
 *  elke opbouw van een pagina opnieuw (getMediaAltTexts in lib/content.ts van de site-repo): zonder
 *  verversing staat een nieuwe beschrijving er na hooguit tien minuten, zoals een pagina. */
export async function wijzigMediaEnVervers(client: WebsiteClient & VerversClient, vorige: MediaBestand, concept: MediaInvoer): Promise<MediaGewijzigd & MetVerversing> {
  const gewijzigd = await wijzigMedia(client, vorige, concept);
  return { ...gewijzigd, verversing: gewijzigd.geschreven.includes('alt_text') ? ververs(client) : null };
}

/* ── Wat de gebruiker leest ────────────────────────────────────────────────── */

export const BEZIG = 'De site wordt bijgewerkt.';
export const NU = 'Staat nu op de site.';
export const LATER = 'Een wijziging staat na hooguit 10 minuten op de site.';
/* De site bewaart de instellingen zelf ook tien minuten: zonder verversing lopen ze één ronde achter
 * de pagina's aan. De site-repo rekent daarom op tien tot twintig minuten
 * (db/voorstel/2026-10-09-getallenbalk.sql). */
export const LATER_INSTELLING = 'Een instelling staat na tien tot twintig minuten op de site.';
/* Hetzelfde geldt voor de doorverwijzingen: de site bewaart de hele tabel tien minuten
 * (getRedirectTable in lib/content.ts van de site-repo). */
export const NU_DOORVERWIJZING = 'Werkt nu op de site.';
export const LATER_DOORVERWIJZING = 'Een doorverwijzing werkt na tien tot twintig minuten op de site.';
/* Na het verwijderen van een doorverwijzing: tot de site haar tabel opnieuw leest, stuurt ze nog door. */
export const NU_VERWIJDERD = 'Het oude adres verwijst nu niet meer door.';
export const LATER_VERWIJDERD = 'Het oude adres verwijst na tien tot twintig minuten niet meer door.';
export const CONCEPT = 'Een concept staat niet op de site.';

/** Eerlijk over wat er gebeurd is: alleen "nu" als de site het zelf bevestigd heeft. `later` is de zin
 *  voor als de site het niet bevestigd heeft, `nu` die voor als ze dat wel deed. */
export const zinNa = (v: Verversing | null, later = LATER, nu = NU): string => (v === null ? CONCEPT : v.uitkomst === 'gelukt' ? nu : later);

interface Melding { title: string; description: string }
/** De vorm van `toast` uit use-toast, zonder die hier te importeren. */
export type Toon = (m: Melding) => { id: string; update: (m: Melding & { id: string }) => void };

/** Toont de melding meteen en werkt hem bij zodra bekend is wat de site deed. Het formulier wacht
 *  daar niet op. `erbij` is een zin die blijft staan, bijvoorbeeld over een doorverwijzing. */
export function meldOpslag(toon: Toon, verversing: Promise<Verversing> | null, title: string, erbij = '', later = LATER, nu = NU): Promise<void> {
  const tekst = (zin: string) => [zin, erbij].filter(Boolean).join(' ');
  if (!verversing) {
    toon({ title, description: tekst(zinNa(null)) });
    return Promise.resolve();
  }
  // "Wordt bijgewerkt" klopt ook als de melding al weg is voordat de uitkomst er is.
  const melding = toon({ title, description: tekst(BEZIG) });
  return verversing.then((v) => melding.update({ id: melding.id, title, description: tekst(zinNa(v, later, nu)) }));
}
